import styles from "./ProductView.module.scss";
import { FC, memo, useEffect, useState } from "react";
import { InView } from "react-intersection-observer";
import { PdpCarouselTargetType, ProductGender } from "src/types/graphql.d";
import usePdpCarousels from "src/hooks/usePdpCarousels";
import { LoadingSpinner } from "src/atoms/LoadingSpinner";
import { ProductCarousel } from "src/molecules/ProductCarousel";
import { CardsLoader, StyledCarousels } from "./ProductView.styled";
import getCollectionsRoute from "src/utils/getCollectionsRoute";
import { locale } from "src/config/app-config";
import { useFeatureFlags } from "src/hooks/useFeatureFlags";
import { GATED_PRODUCT_PAGE } from "src/constants/gatedItem";
import {
  CAROUSEL_CONTENT_TYPE,
  PRODUCT_CAROUSEL_SLIDES,
} from "src/utils/constants";

interface ProductViewCarouselsProps {
  slug: string;
  shopType: ProductGender;
}

const Carousel = memo(ProductCarousel);

const ProductViewCarousels: FC<ProductViewCarouselsProps> = ({
  shopType,
  slug,
}) => {
  const { enableWishListCarousel } = useFeatureFlags();
  const { data, loading } = usePdpCarousels({
    slug,
    shopType,
    fetchPolicy: "cache-and-network",
  });
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const getViewAllLink = (
    targetType: PdpCarouselTargetType,
    targetLink: string
  ) => {
    return targetType === PdpCarouselTargetType.CollectionPlp
      ? getCollectionsRoute({
          shopType,
          collectionsSlug: targetLink,
          locale,
        })
      : "";
  };

  const pdpCarousels = enableWishListCarousel
    ? data?.pdpCarousels?.carousels
    : data?.pdpCarousels?.carousels?.filter(
        (carousel) => carousel.id !== "pdp_favorite_items_carousel"
      );

  return (
    <InView rootMargin={"150px 0px 0px 0px"} triggerOnce={true}>
      {({ inView, ref }) => (
        <div className={styles.carousels} ref={ref}>
          {loading && isClient ? (
            <CardsLoader>
              <LoadingSpinner />
            </CardsLoader>
          ) : (
            pdpCarousels?.map(
              (carousel, carouselIndex) =>
                (inView || !isClient) && (
                  <StyledCarousels key={carousel.id} data-testid={carousel.id}>
                    <Carousel
                      genderId={shopType}
                      title={carousel.title}
                      products={carousel.products}
                      titleForTracking={carousel.title}
                      shouldReset={true}
                      slidesDesktop={PRODUCT_CAROUSEL_SLIDES.desktop}
                      slidesTablet={PRODUCT_CAROUSEL_SLIDES.tablet}
                      slidesMobile={PRODUCT_CAROUSEL_SLIDES.mobile}
                      marginSizeMobile="l"
                      headerLinkHref={
                        carousel.target_link && carousel.target_type
                          ? getViewAllLink(
                              carousel.target_type,
                              carousel.target_link
                            )
                          : ""
                      }
                      responseSourceType={carousel.response_source_type}
                      cnstrcPodId={carousel.pod_id ?? undefined}
                      cnstrcResultId={carousel.result_id ?? undefined}
                      cnstrcStrategyId={carousel.strategy_id ?? undefined}
                      trackingData={{
                        shopType,
                        pageName: GATED_PRODUCT_PAGE,
                        carouselType: carousel.id,
                      }}
                      carouselTrackingData={{
                        carouselId: carousel.id,
                        carouselPosition: carouselIndex + 1,
                        contentType: CAROUSEL_CONTENT_TYPE.PRODUCT,
                        shopType,
                        targetLink: carousel.target_link || undefined,
                        targetType: carousel.target_type || undefined,
                        responseSourceType: carousel.response_source_type,
                      }}
                      hideViewAll
                    />
                  </StyledCarousels>
                )
            )
          )}
        </div>
      )}
    </InView>
  );
};

export default ProductViewCarousels;
